// Confirmation-email preview — RON-5466. Prototype: `EmailFrame`, `EmailMap`, `buildVisitorEmail`.
//
// A read-only preview of what the visitor will receive. The template itself is admin-managed
// (`tblvisitor_email_template`, §7) and cannot be edited here; merge fields are filled in
// automatically. Sending happens server-side through Ronspot's existing SES setup.
//
// REBUILT 08-October-2026 to match the prototype frame: logo band, From / Reply-to, the prose
// body, the embedded map and the Ronspot footer. It previously showed a label/value table, which
// is not what a visitor receives.
//
// ── Two things this preview CANNOT be truthful about, and says so rather than faking ──────────
//
// 1. THE QR CODE. It encodes the `booking_code` — a plain 8-character string like `ABCD2345`,
//    from `ABCDEFGHJKLMNPQRSTUVWXYZ23456789` (no I, O, 0 or 1, because it gets read off a phone
//    and typed). The kiosk scans it straight into `POST /visitor/kiosk/scan`.
//
//    THE CODE DOES NOT EXIST YET AT THIS POINT. It is allocated server-side on create, and this
//    screen runs before the visit is created — so there is nothing to encode. Not a library
//    problem; a sequencing one. The prototype draws a decorative pattern here; we show a labelled
//    placeholder instead, because a barcode that fails when somebody scans it is worse than an
//    obvious placeholder. The backend fills `{{qr_code}}` in the real email.
//
// 2. THE BODY TEXT. The real subject and body come from the admin-managed template
//    (`GET /visitor/emails/visitor_invite`, endpoint 42 — already built) with merge fields
//    `{{date}} {{host}} {{location}} {{map}} {{qr_code}} {{time}} {{visitor_name}}`. What is
//    written here is the SHIPPED DEFAULT, so it is right until an admin customises it, and wrong
//    afterwards. Fetching the real template is the correct fix and is noted in the module docs.

import { useTranslation } from "react-i18next";

import ronspotLogo from "../../../assets/images/ronspot_green_logo.svg";
import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import { VISIT_TYPE } from "../visitorConstants.js";

const hasCoordinates = (site) =>
  typeof site?.lat === "number" && typeof site?.lng === "number";

const directionsUrl = (site) =>
  hasCoordinates(site)
    ? `https://www.google.com/maps/dir/?api=1&destination=${site.lat},${site.lng}`
    : null;

const embedUrl = (site) =>
  hasCoordinates(site)
    ? `https://www.google.com/maps?q=${site.lat},${site.lng}&z=15&output=embed`
    : null;

// `contractor` is OPTIONAL and absent for a visitor invite, which is the whole of the difference
// between the two emails. When present the body gains a Work line and its closing paragraph
// branches on whether vetting is complete — see below.
//
// One component rather than two, because the frame, the logo band, the From/Reply-to rows, the
// map and the footer are identical and were rebuilt once already on 08-October to match the
// prototype. A second copy would be a second thing to keep in step.
const VisitorEmailPreview = ({ form, site, building, visitors, contractor = null }) => {
  const { t } = useTranslation("common");

  const names = visitors
    .map((visitor) => visitor.name.trim())
    .filter(Boolean)
    .join(", ");

  const isMultiDay = form.startDate !== form.endDate;
  const dateLabel =
    formatDateToEEEddMMM(form.startDate) +
    (isMultiDay ? ` – ${formatDateToEEEddMMM(form.endDate)}` : "");

  const siteName = site?.name || "";
  const guestNames = names || t("react_visitors_unnamed");

  // The shipped default template, assembled the way the prototype's `buildVisitorEmail` does.
  // Kept as ONE string so it renders with `white-space: pre-wrap`, exactly as an email body
  // would — a list of <p> elements would re-wrap differently from what the visitor receives.
  const body = [
    t("react_visitors_email_greeting").replace("%s", guestNames),
    "",
    t("react_visitors_email_invited")
      .replace("%s", siteName)
      .replace("%h", form.host || ""),
    "",
    t("react_visitors_email_details_heading"),
    `  ${t("date")}: ${dateLabel}`,
    `  ${t("react_visitors_field_arrival")}: ${form.arrivalTime}`,
    `  ${t("react_visitors_field_site")}: ${siteName}`,
    building ? `  ${t("react_visitors_field_building")}: ${building.name}` : null,
    // CONTRACTOR ONLY — the prototype's `Work: ` line, with its own fallback. A contractor email
    // that did not say what they were coming to do would be missing the one detail the desk
    // actually needs when they arrive.
    contractor
      ? `  ${t("react_visitors_email_work_label")}: ${
          form.workDescription?.trim() || t("react_visitors_email_work_fallback")
        }`
      : null,
    `  ${t("react_visitors_field_host")}: ${form.host || ""}`,
    form.type === VISIT_TYPE.VIP
      ? `  ${t("react_visitors_col_type")}: ${t("react_visitors_vip")}`
      : null,
    "",
    // THE CLOSING PARAGRAPH — the one place the two emails genuinely diverge.
    //
    // A contractor whose vetting is NOT complete is told a compliance pack follows and that they
    // cannot be admitted, and gets NO QR CODE. A QR implies they can walk up to the kiosk and be
    // let in; they cannot, and the kiosk will refuse them. Showing one would be the single most
    // misleading thing this email could do.
    //
    // A vetted contractor is told until when, and gets the QR like any visitor.
    ...(contractor?.isProvisional
      ? [t("react_visitors_email_contractor_provisional").replace("%s", form.host || "")]
      : [
          contractor?.vettedUntil
            ? t("react_visitors_email_contractor_vetted").replace(
                "%s",
                formatDateToEEEddMMM(contractor.vettedUntil)
              )
            : t("react_visitors_email_questions").replace("%s", form.host || ""),
          "",
          t("react_visitors_email_show_qr"),
        ]),
  ]
    .filter((line) => line !== null)
    .join("\n");

  return (
    <div className="visitor-email">
      <div className="visitor-email__brand">
        <img src={ronspotLogo} alt="Ronspot" className="visitor-email__logo" />
      </div>

      {/* From is Ronspot's no-reply address; Reply-to is the host, so a visitor who answers the
          email reaches the person expecting them rather than an unmonitored mailbox. */}
      <div className="visitor-email__meta">
        <div className="visitor-email__meta-row">
          <span className="visitor-email__meta-label">{t("react_visitors_email_from")}</span>
          <span>
            {t("react_visitors_email_from_name").replace("%s", siteName)} &lt;
            {t("NoReplyEmailAddress")}&gt;
          </span>
        </div>
        <div className="visitor-email__meta-row">
          <span className="visitor-email__meta-label">{t("react_visitors_email_reply_to")}</span>
          <span>{form.host || "—"}</span>
        </div>
      </div>

      <p className="visitor-email__subject">
        {t("react_visitors_email_subject")
          .replace("%s", siteName)
          .replace("%d", formatDateToEEEddMMM(form.startDate))}
      </p>

      <p className="visitor-email__body">{body}</p>

      {/* Hidden for a contractor whose vetting is not complete — the same reasoning as the body
          above. The placeholder is not merely decorative: it stands where a scannable code will
          be, and showing it to somebody the kiosk will refuse is a promise this email cannot
          keep. */}
      {!contractor?.isProvisional && (
        <div className="visitor-email__qr">
          <span className="visitor-email__qr-box" aria-hidden="true">
            {t("react_visitors_email_qr_placeholder")}
          </span>
          <p className="visitor-email__qr-note">{t("react_visitors_email_qr_note")}</p>
        </div>
      )}

      {/* "Getting here" — the same arrangement as the reception site picker: the frame is
          conditional on real coordinates, lazy, and sends no referrer. A site without a location
          gets the address and the link, never an empty grey rectangle where a map should be.

          NOTE FOR THE EMAIL ITSELF (RON-5470): email clients strip iframes. The real message
          needs a STATIC map image (Google Static Maps API + key) — the backend fills `{{map}}`.
          This embed is preview-only and must not be copied into the template. */}
      {(site?.address || hasCoordinates(site)) && (
        <div className="visitor-email__map">
          <p className="visitor-email__map-title">{t("react_visitors_email_getting_here")}</p>

          <div className="visitor-email__map-frame">
            {hasCoordinates(site) && (
              <iframe
                className="visitor-email__map-embed"
                title={t("react_visitors_email_map_title").replace("%s", siteName)}
                loading="lazy"
                referrerPolicy="no-referrer"
                src={embedUrl(site)}
              />
            )}

            <div className="visitor-email__map-foot">
              <span className="visitor-email__map-where">
                <strong>{siteName}</strong>
                {site?.address ? ` · ${site.address}` : ""}
              </span>
              {directionsUrl(site) && (
                <a
                  className="visitor-email__map-link"
                  href={directionsUrl(site)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("react_visitors_email_directions")}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="visitor-email__footer">
        {t("react_visitors_email_sent_securely").replace("%s", siteName)}
      </p>
    </div>
  );
};

export default VisitorEmailPreview;
